import { useCallback, useEffect, useState } from 'react';
import { driver } from 'driver.js';

/**
 * Sprint 9.1 — Hook de tours contextuales / autocapacitación.
 *
 * Envuelve `driver.js` con persistencia en `ConfiguracionSistema`
 * (clave `tour_seen_<id>` = "true"/"false"). Da una API minimalista a
 * los callers: `runTour()` para disparar y `hasSeenTour` para mostrar
 * el badge "Nuevo" la primera vez.
 *
 * Fail-open: si el handler de config falla, `hasSeenTour=false` (badge
 * visible) y el tour igual corre. La ayuda nunca se debe a sí misma.
 */

export interface TourStep {
  /** Selector CSS del elemento a destacar. Convención: `[data-tour="<key>"]`. */
  element: string;
  popover: {
    title: string;
    description: string;
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
  };
}

export interface TourDefinition {
  /** Identificador estable. Convención: snake_case (ej `corte_caja`). */
  id: string;
  /** Texto humano para el header del botón de ayuda. */
  title: string;
  steps: TourStep[];
}

export interface UseTourResult {
  hasSeenTour: boolean;
  loading: boolean;
  runTour: () => void;
  /** Marcado manual (rara vez necesario; `runTour` ya lo hace al completar). */
  markAsSeen: () => Promise<void>;
}

const keyFor = (id: string) => `tour_seen_${id}`;

export function useTour(def: TourDefinition): UseTourResult {
  const [hasSeenTour, setHasSeenTour] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await window.api.config.obtener(keyFor(def.id));
        if (!alive) return;
        if (res.ok) {
          setHasSeenTour(res.data === 'true');
        }
      } catch {
        // Fail-open: badge visible si no podemos consultar.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [def.id]);

  const markAsSeen = useCallback(async () => {
    try {
      await window.api.config.actualizar(keyFor(def.id), 'true');
    } catch {
      // Persistencia opcional; no romper la UI si falla.
    }
    setHasSeenTour(true);
  }, [def.id]);

  const runTour = useCallback(() => {
    const d = driver({
      showProgress: true,
      progressText: 'Paso {{current}} de {{total}}',
      nextBtnText: 'Siguiente →',
      prevBtnText: '← Anterior',
      doneBtnText: 'Listo, ya entendí',
      // Cuando el tour termina (no se cancela), se marca como visto.
      onDestroyed: () => {
        void markAsSeen();
      },
      steps: def.steps.map((s) => ({
        element: s.element,
        popover: {
          title: s.popover.title,
          description: s.popover.description,
          side: s.popover.side,
          align: s.popover.align,
        },
      })),
    });
    d.drive();
  }, [def.steps, markAsSeen]);

  return { hasSeenTour, loading, runTour, markAsSeen };
}
